/**
 * Flight layover classification rules — single source of truth for both
 * filtering (filter.vue) and display (flightOrder.vue / booking.vue / etc.).
 *
 * Two overnight rules, matched bit-for-bit with the legacy inline logic in
 * filter.vue::dayAirportLayover so this refactor is behavior-preserving:
 *
 *   Rule A — Arrived in [04:00, 21:59] AND next departure is AFTER the
 *            arrival-date + 1 day at 04:00:00 local. "Daytime arrival but
 *            traveller has to wait into the next morning" = real overnight.
 *
 *   Rule B — Arrived in [22:00, 23:59] or [00:00, 03:59] (night-window),
 *            AND layover duration ≥ 7 hours. "Arrived at night and the next
 *            flight is many hours away" = real overnight.
 *
 * The two windows are mutually exclusive on arrival hour, so a single layover
 * fires AT MOST one rule. If neither fires, the layover is daytime/short.
 */

export interface LayoverSegment {
  arrival: { at: string; iataCode: string }
  departure: { at: string; iataCode: string }
  /** Marketing carrier code, e.g. "JL", "AA". Optional — when both sides have it
   *  and they differ, the layover is flagged as a self-transfer / carrier change. */
  carrierCode?: string
}

export type OvernightRule = 'A' | 'B' | null

export interface OvernightResult {
  isOvernight: boolean
  rule: OvernightRule
}

export interface LayoverClassification {
  /** Hours between arrival and next departure (can be negative if data is bad). */
  durationHours: number
  /** True when arrival airport IATA ≠ next departure airport IATA. */
  airportMismatch: boolean
  /** True when both segments have a carrierCode and they differ. */
  carrierChange: boolean
  /** True when Rule A or Rule B fires. */
  isOvernight: boolean
  /** Which overnight rule fired, or null. */
  overnightRule: OvernightRule
  /** True for long but non-overnight waits (≥ `longWaitHours`, default 5h). */
  isLongWait: boolean
}

export interface ClassifyOptions {
  /** Threshold (hours) for the "long wait" classification when not overnight.
   *  Default 5. */
  longWaitHours?: number
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

/**
 * Pure overnight check — used by both the filter pipeline and the display
 * layer. Returns `{ isOvernight: false, rule: null }` for invalid inputs.
 */
export function isOvernightLayover(
  arrivalAt: Date | string | number,
  nextDepartureAt: Date | string | number,
): OvernightResult {
  const arrAt = toDate(arrivalAt)
  const depAt = toDate(nextDepartureAt)

  if (isNaN(arrAt.getTime()) || isNaN(depAt.getTime())) {
    return { isOvernight: false, rule: null }
  }

  const arrHour = arrAt.getHours()
  const layoverHours = (depAt.getTime() - arrAt.getTime()) / 3_600_000

  // Rule A — daytime arrival, departure crosses next-day 04:00.
  if (arrHour < 22 && arrHour >= 4) {
    const next4am = new Date(arrAt)
    next4am.setDate(next4am.getDate() + 1)
    next4am.setHours(4, 0, 0, 0)
    if (depAt.getTime() > next4am.getTime()) {
      return { isOvernight: true, rule: 'A' }
    }
    return { isOvernight: false, rule: null }
  }

  // Else (arrHour ≥ 22 or arrHour < 4) — night arrival, ≥ 7h layover.
  if (layoverHours >= 7) {
    return { isOvernight: true, rule: 'B' }
  }
  return { isOvernight: false, rule: null }
}

/**
 * Full classification used by display components. Returns all the flags
 * needed to render layover badges and hints in one shot.
 */
export function classifyLayover(
  arrivalSegment?: LayoverSegment | null,
  nextDepartureSegment?: LayoverSegment | null,
  options: ClassifyOptions = {},
): LayoverClassification {
  const longWaitHours = options.longWaitHours ?? 5

  const arrAt = new Date(arrivalSegment?.arrival?.at ?? '')
  const depAt = new Date(nextDepartureSegment?.departure?.at ?? '')
  const hasValidTimes = !isNaN(arrAt.getTime()) && !isNaN(depAt.getTime())
  const durationHours = hasValidTimes
    ? (depAt.getTime() - arrAt.getTime()) / 3_600_000
    : 0

  const arrivalAirport = arrivalSegment?.arrival?.iataCode
  const departureAirport = nextDepartureSegment?.departure?.iataCode
  const airportMismatch =
    !!(arrivalAirport && departureAirport && arrivalAirport !== departureAirport)

  const c1 = arrivalSegment?.carrierCode
  const c2 = nextDepartureSegment?.carrierCode
  const carrierChange = !!(c1 && c2 && c1 !== c2)

  const overnight = hasValidTimes
    ? isOvernightLayover(arrAt, depAt)
    : { isOvernight: false, rule: null }
  const isLongWait = !overnight.isOvernight && durationHours >= longWaitHours

  return {
    durationHours,
    airportMismatch,
    carrierChange,
    isOvernight: overnight.isOvernight,
    overnightRule: overnight.rule,
    isLongWait,
  }
}

/**
 * Format a duration in hours as e.g. "1H 48M". Mirrors flightOrder.vue's
 * `timeFormat` so display strings stay consistent across components.
 */
export function formatLayoverDuration(hours: number): string {
  if (!isFinite(hours) || hours < 0) return ''
  const total = Math.round(hours * 60)
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0 && m === 0) return '0M'
  const hStr = h > 0 ? `${h}H` : ''
  const mStr = m > 0 ? `${m}M` : ''
  return hStr && mStr ? `${hStr} ${mStr}` : hStr || mStr
}
